import React, { Component } from 'react';
import Axios from 'axios';
import PropTypes from 'prop-types';
import { injectStateService } from '@/shared/services/StateService';
import { dynamicSort } from '@/shared/utils/dynamicSort';

import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import SimplePanel from '@/shared/components/SimplePanel';
import api from '@/shared/api';
import { FaCog } from 'react-icons/fa';
import eventBus from 'pubsub-js';
import ToastrService from '@/shared/services/ToastrService';
import Button from '@/shared/components/Button';
import BatchRegistrationModal from '@/shared/components/registration/BatchRegistrationModal';
import SendEmailModal from '@/shared/components/email/MultiRelationsMailModal';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import handleHttpError from '@/shared/services/HandleHttpError';
import { CanCreate, CanUpdate, showWhenPermission } from '@/shared/components/security';
import BatchGenerateDocumentModal from '@/shared/components/BatchGenerateDocumentModal';
import {
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import DataCacheService from '@/shared/services/DataCacheService';
import SignalRService from '@/shared/services/SignalRService';
import YesNoModal from '../../../shared/components/YesNoModal';
import config from '../../../config';
import Checkbox from '../../../shared/components/input/Checkbox';
import RelationOverviewRow, { getColumnDefinitions } from './RelationOverviewRow';
import UpdateTaskScheduleModal from './modals/UpdateTaskScheduleModal';
import RelationOverviewFilters, { defaultRelationFilters } from './RelationOverviewFilters';
import UpdateRelationTeamModal from './modals/UpdateRelationTeamModal';
import UpdateRelationTagsModal from './modals/UpdateRelationTagsModal';
import UpdateRelationUserModal from './modals/UpdateRelationUserModal';
import SearchRelationsInPeppolNetworkModal from './modals/SearchRelationsInPeppolNetworkModal';
import FiscalReportModal from './modals/FiscalReportModal';

import messages from './relationOverviewMessages';
import GlobalActionsButton from './GlobalActionsButton';
import UpdateRelationFieldModal from './modals/UpdateRelationFieldModal';

import SetInvoiceScheduleModal from './modals/SetInvoiceScheduleModal';
import CreatePlannedTaskModal from './modals/CreatePlannedTaskModal';
import AddAssignmentModal from './modals/AddAssignmentModal';
import ExportButton from './ExportButton';

const columnSelectionStorageKey = 'RelationOverviewHiddenColumns';

class RelationOverviewContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const { relationFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem('RelationOverviewParams')) || {};

    const columnDefinitions = getColumnDefinitions(DataCacheService.getTenantConfiguration());
    const hiddenColumnsState = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      intl,
    );
    this.state = {
      columnDefinitions,
      showDeleteRelationModal: false,
      showUpdateTaskScheduleModal: false,
      showUpdateRelationFieldModal: false,
      showCreateRegistrationModal: false,
      showSetInvoiceScheduleModal: false,
      showCreatePlannedTaskModal: false,
      showUpdateRelationTagsModal: false,
      showUpdateRelationUserModal: false,
      showAddAssignmentModal: false,
      showGenerateDocumentsModal: false,
      showFiscalReportModal: false,
      showSendMailModal: false,
      activeRelation: undefined,
      relationLanguages: [],
      labels: [],
      items: [],
      allRelationsSelected: false,
      currentSearchParams: undefined,
      ...hiddenColumnsState,
      initialSearchParams: {
        // Set default filters
        sortKey: 'displayname',
        pageSize: 50,
        relationFilters: {
          ...defaultRelationFilters,
          ...(!!Object.keys(searchParams).length ? {} : sessionFilters),
          ...searchParams,
        },
        // Override with session storage params, but only if we didnt get redirected from another screen via onclick
        ...(!!Object.keys(searchParams).length ? {} : sessionParams),
      },
    };
  }

  componentDidMount() {
    this.tagsCancelToken = Axios.CancelToken.source();
    api.base
      .get(`${config.appUrls.api}/api/Resource/Tags`, {
        cancelToken: this.tagsCancelToken.token,
      })
      .then(({ data }) => {
        this.setState({
          labels: data
            .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
            .sort(dynamicSort('sort')),
        });
      })
      .catch(error => {
        // Only handle non-cancellation errors
        if (!Axios.isCancel(error)) {
          console.error('Error loading tags:', error);
        }
      });
  }

  componentWillUnmount() {
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to leaving page.');
    }
    if (this.tagsCancelToken && !this.tagsCancelToken.token.reason) {
      this.tagsCancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(items, hideableColumns, columnSelectionStorageKey),
      showColumnSelectionModal: false,
    }));
  };

  deleteRelation = () => {
    const { activeRelation, items } = this.state;
    const { intl } = this.props;
    const endpoint = `${config.appUrls.api}/api/relation/delete/${activeRelation.id}`;

    return api.base
      .delete(endpoint)
      .then(
        ({ data }) =>
          new Promise(resolve => {
            this.setState(state => ({
              ...state,
              showDeleteRelationModal: false,
              items: items.filter(relation => relation.id !== activeRelation.id),
            }));

            // Server returns true if the relation was put on inactive because there are open links/invoices/registrations...
            if (data) {
              ToastrService.info(intl.formatMessage(messages.RelationPutInactive));
            } else {
              ToastrService.changesSaved(intl);
            }
            eventBus.publish('reloadRecentRelations');
            resolve(data);
          }),
      )
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  showDeleteRelationConfirmation = relation => {
    this.setState(state => ({
      ...state,
      showDeleteRelationModal: true,
      activeRelation: relation,
    }));
  };

  showUpdateTaskScheduleModal = () => {
    this.setState(state => ({
      ...state,
      showUpdateTaskScheduleModal: true,
    }));
  };

  showSetInvoiceScheduleModal = () => {
    this.setState(state => ({
      ...state,
      showSetInvoiceScheduleModal: true,
    }));
  };
  showAddAssignmentModal = () => {
    this.setState(state => ({
      ...state,
      showAddAssignmentModal: true,
    }));
  };
  showUpdateRelationUserModal = () => {
    this.setState(state => ({
      ...state,
      showUpdateRelationUserModal: true,
    }));
  };

  showUpdateRelationFieldModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showUpdateRelationFieldModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  showCreateRegistrationModal = () => {
    this.setState(state => ({
      ...state,
      showCreateRegistrationModal: true,
    }));
  };
  showCreatePlannedTaskModal = () => {
    this.setState(state => ({
      ...state,
      showCreatePlannedTaskModal: true,
    }));
  };
  isGlobalActionsDisabled = () => {
    const { items, allRelationsSelected } = this.state;
    if (!items || !items.length) return true;
    if (items.some(x => x.selected) || allRelationsSelected) return false;

    return true;
  };
  getSelectionParams = () => {
    const { items, allRelationsSelected } = this.state;

    // Get all relation ids that don't match the select all checkbox
    const relationIds = items
      ? items.filter(x => x.selected !== allRelationsSelected).map(x => x.id)
      : [];
    return {
      relationIds,
      selectAll: allRelationsSelected,
    };
  };

  handleReportResponse = () => {
    const { intl } = this.props;

    this.setState({ exporting: false });
    ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
  };
  handleReportError = error => {
    const { intl } = this.props;
    this.setState({ exporting: false });
    handleHttpError(intl)(error);
  };

  createAuthorizationReport = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const params = this.getSelectionParams();
    const url = `/api/relationbatch/AuthorizationReport`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,
      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(url, params, { params: urlParams })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };

  createExcelReport = () => {
    const { currentSearchParams: searchParams } = this.state;
    const { intl } = this.props;

    const params = this.getSelectionParams();
    const url = `/api/relationbatch/Report`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,
      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(url, params, { params: urlParams })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };
  createLinkedRelationsReport = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const params = this.getSelectionParams();
    const url = `/api/relationbatch/LinkedRelationsReport`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,
      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(url, params, { params: urlParams })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };

  createFiscalReport = modalParams => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const params = { ...this.getSelectionParams(), ...modalParams };
    const url = `/api/Fiscal/Report`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,
      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(url, params, { params: urlParams })
      .then(this.handleReportResponse)
      .then(() => {
        this.setState({ showFiscalReportModal: false });
      })
      .catch(this.handleReportError);
  };

  createCommunicationChannelReport = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const params = this.getSelectionParams();
    const url = `/api/relationbatch/CommunicationChannelReport`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,

      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(url, params, { params: urlParams })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };

  createCommunicationSettingReport = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const params = this.getSelectionParams();
    const url = `/api/relationbatch/CommunicationSettingReport`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,

      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(url, params, { params: urlParams })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };

  executeBatchAction = ({ action, parameters }) => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;
    const { stateService } = this.props;

    let endpoint = '';
    let successMessage = '';
    let params = {};
    let newLabels = [];

    switch (action) {
      case 'fieldupdate':
        endpoint = 'RelationFieldUpdate';
        successMessage = intl.formatMessage(messages.UpdateFieldQueued);
        params = { field: parameters };
        break;
      case 'createtaskschedule':
        endpoint = 'CreateBatchTaskSchedule';
        successMessage = intl.formatMessage(messages.SetTaskSchedulingQueued);
        params = { taskSchedule: parameters };
        break;
      case 'removetaskschedule':
        endpoint = 'RemoveBatchTaskSchedule';
        successMessage = intl.formatMessage(messages.DeleteTaskScheduleQueued);
        params = { taskSchedule: parameters };
        break;
      case 'createregistration':
        endpoint = 'CreateBatchRegistration';
        successMessage = intl.formatMessage(messages.NewRegistrationsQueued);
        params = { parameters };
        break;
      case 'setinvoiceschedule':
        endpoint = 'SetBatchInvoiceSchedule';
        successMessage = intl.formatMessage(messages.SetInvoiceScheduleQueued);
        params = { invoiceSchedule: parameters };
        break;
      case 'createplannedtask':
        endpoint = 'CreateBatchTask';
        successMessage = intl.formatMessage(messages.CreateTaskQueued);
        params = { task: parameters };
        break;
      case 'sendmail':
        endpoint = 'CreateBatchMailJob';
        successMessage = intl.formatMessage(messages.SendEmailQueued);
        params = { ...parameters };
        break;
      case 'teamsupdate':
        endpoint = 'RelationTeamsUpdate';
        if (parameters.action === 'add') {
          successMessage = intl.formatMessage(messages.AddTeamToRelationQueued);
        } else if (parameters.action === 'remove') {
          successMessage = intl.formatMessage(messages.RemoveTeamFromRelationQueued);
        }
        params = { ...parameters };
        break;
      case 'tagsupdate':
        endpoint = 'RelationTagsUpdate';
        if (parameters.action === 'add') {
          successMessage = intl.formatMessage(messages.AddTagsToRelationQueued);
          if (parameters && parameters.tags && parameters.tags.length) {
            const { labels } = this.state;
            // these new labels should be added to state array after request finishes
            parameters.tags.map((x, index) => {
              if (labels.indexOf(x) === -1) {
                const newLabel = {
                  id: labels.length + index + 1,
                  label: x,
                };
                newLabels = [...newLabels, newLabel];
              }
            });
          }
        } else if (parameters.action === 'remove') {
          successMessage = intl.formatMessage(messages.RemoveTagsFromRelationQueued);
        }
        params = { ...parameters };
        break;
      case 'softwareupdate':
        endpoint = 'RelationSoftwareUpdate';
        successMessage = intl.formatMessage(messages.BackgroundJobQueued);
        params = { ...parameters };
        break;
      case 'addAssignment':
        endpoint = 'CreateBatchAssignment';
        successMessage = intl.formatMessage(messages.BackgroundJobQueued);
        params = { assignment: parameters };
        break;
      case 'relationUserUpdate':
        endpoint = 'RelationUserUpdate';
        successMessage = intl.formatMessage(messages.BackgroundJobQueued);
        params = { ...parameters };
        break;
      case 'generateDocuments':
        endpoint = 'GenerateDocuments';
        successMessage = intl.formatMessage(messages.GenerateDocumentsQueued);
        params = { ...parameters };
        break;
      default:
        endpoint = action;
        successMessage = intl.formatMessage(messages.BackgroundJobQueued);
        params = { ...parameters };
        break;
    }

    params = { ...params, ...this.getSelectionParams() };

    const url = `/api/relationbatch/${endpoint}`;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...searchParams.relationFilters,
    };

    return api.base
      .post(url, params, { params: urlParams })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showUpdateTaskScheduleModal: false,
          showUpdateRelationFieldModal: false,
          showCreateRegistrationModal: false,
          showSetInvoiceScheduleModal: false,
          showCreatePlannedTaskModal: false,
          showUpdateRelationTeamModal: false,
          showUpdateRelationTagsModal: false,
          showUpdateRelationUserModal: false,
          showAddAssignmentModal: false,
          showGenerateDocumentsModal: false,
          showSendMailModal: false,
          showSearchRelationsInPeppolNetworkModal: false,
          labels: [...state.labels, ...newLabels],
        }));

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(successMessage, undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  createTaskScheduleForSelectedRelations = taskSchedule =>
    this.executeBatchAction({
      action: 'createtaskschedule',
      parameters: taskSchedule,
    });

  removeTaskScheduleForSelectedRelations = taskSchedule =>
    this.executeBatchAction({
      action: 'removetaskschedule',
      parameters: taskSchedule,
    });

  setInvoiceScheduleSelectedRelations = invoiceSchedule =>
    this.executeBatchAction({
      action: 'setinvoiceschedule',
      parameters: invoiceSchedule,
    });

  addAssignmentToSelectedRelations = assignment =>
    this.executeBatchAction({
      action: 'addAssignment',
      parameters: assignment,
    });

  updateRelationField = (field, endpoint) =>
    this.executeBatchAction({
      action: endpoint ? endpoint : 'fieldupdate',
      parameters: field,
    });

  updateRelationTeam = data =>
    this.executeBatchAction({
      action: 'teamsupdate',
      parameters: data,
    });

  updateRelationTags = data =>
    this.executeBatchAction({
      action: 'tagsupdate',
      parameters: data,
    });

  createBatchRegistrations = batchRegistrationModel =>
    this.executeBatchAction({
      action: 'createregistration',
      parameters: batchRegistrationModel,
    });
  createTaskForSelectedRelations = task =>
    this.executeBatchAction({
      action: 'createplannedtask',
      parameters: task,
    });
  sendEmailForSelectedRelations = email =>
    this.executeBatchAction({
      action: 'sendmail',
      parameters: email,
    });
  updateRelationUser = data =>
    this.executeBatchAction({
      action: 'relationUserUpdate',
      parameters: data,
    });

  generateDocuments = data =>
    this.executeBatchAction({
      action: 'generateDocuments',
      parameters: data,
    });

  restoreRelation = relation => {
    const endpoint = `${config.appUrls.api}/api/relation/undelete/${relation.id}`;
    const { items } = this.state;
    const { intl } = this.props;

    return api.base
      .delete(endpoint)
      .then(() => {
        const itemsCopy = [...items];

        const item = itemsCopy.find(x => x.id === relation.id);

        item.isActive = true;

        this.setState(state => ({
          ...state,
          items: itemsCopy,
        }));

        ToastrService.changesSaved(intl);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  newRelation = () => {
    const { stateService } = this.props;
    stateService.goToNewRelation();
  };

  goToInteractions = (relationId, openMailModal) => {
    const { stateService } = this.props;
    stateService.goToInteractions(relationId, openMailModal);
  };

  handleRelationSelect = relation => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === relation.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  handleSelectAllRelations = () => {
    const { items, allRelationsSelected } = this.state;

    const isCurrentlySelected = !allRelationsSelected;

    const itemsCopy = items.map(e => ({ ...e, selected: !!isCurrentlySelected }));

    this.setState(state => ({
      ...state,
      allRelationsSelected: isCurrentlySelected,
      items: itemsCopy,
    }));
  };

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        relationFilters: filters,
      },
    }));
  };

  loadItems = searchParams => {
    const { allRelationsSelected } = this.state;
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to new request.');
    }
    this.searchToken = Axios.CancelToken.source();

    sessionStorage.setItem(
      'RelationOverviewParams',
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        relationFilters: searchParams.relationFilters,
      }),
    );

    return api.base
      .get('/api/relation/list', {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
          ...searchParams.relationFilters,
        },
        cancelToken: this.searchToken.token,
      })
      .then(({ data }) => {
        // If checkbox for all relations is selected, set selected on all new incoming relations
        const newRelations = data.data.map(e => ({ ...e, selected: !!allRelationsSelected }));

        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...newRelations] : [...newRelations],
          total: data.total,
          currentSearchParams: searchParams,
        }));

        return data.data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  getSelectedRelationLanguages = () => {
    const { allRelationsSelected, items } = this.state;
    return new Promise((resolve, reject) => {
      if (allRelationsSelected) {
        const { currentSearchParams: searchParams } = this.state;

        const params = { ...this.getSelectionParams() };

        const urlParams = {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
          ...searchParams.relationFilters,
        };

        api.base
          .post(`/api/relation/OverviewLanguages`, params, { params: urlParams })

          .then(({ data }) => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        const languages = [...new Set(items.filter(x => x.selected).map(x => x.languageCode))];
        resolve(languages);
      }
    });
  };

  openEmailModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showSendMailModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  openGenerateDocumentsModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showGenerateDocumentsModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  getCountSelectedRelationsFormatted() {
    const { total } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelectedRelations();

    return intl.formatMessage(messages.CountRelationsSelected, { count, total });
  }

  getCountSelectedRelations() {
    const { allRelationsSelected, total, items } = this.state;

    const count = items ? items.filter(x => x.selected !== allRelationsSelected).length : 0;

    if (allRelationsSelected) {
      return total - count;
    }
    return count;
  }

  determineRelationTypeForGlobalAction = () => {
    const {
      allRelationsSelected,
      items,
      initialSearchParams: { relationFilters = {} } = {},
    } = this.state;
    if (relationFilters && relationFilters.type && relationFilters.type.length === 1) {
      return [relationFilters.type]; // Return relation type from filter if only 1 is selected
    }
    if (allRelationsSelected) return null; // Cannot determine relation type if select all is on
    if (!items || items.length <= 0) return null;
    const [first, ...selected] = items.filter(x => x.selected);
    const relationType = first.type; // Get relation type from first selected
    if (selected.some(x => x.type !== relationType)) return null; // If there are there other relation types selected, return null
    return relationType;
  };

  render() {
    const {
      initialSearchParams,
      items,
      showDeleteRelationModal,
      showUpdateTaskScheduleModal,
      showUpdateRelationFieldModal,
      showCreateRegistrationModal,
      showSetInvoiceScheduleModal,
      showSearchRelationsInPeppolNetworkModal,
      showCreatePlannedTaskModal,
      showUpdateRelationTeamModal,
      showUpdateRelationTagsModal,
      showUpdateRelationUserModal,
      showGenerateDocumentsModal,
      showFiscalReportModal,
      showSendMailModal,
      showAddAssignmentModal,
      allRelationsSelected,
      exporting,
      relationLanguages,
      labels,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
      columnDefinitions,
    } = this.state;
    const { intl } = this.props;

    const hasData = items && items.length > 0;

    return (
      <div>
        {showDeleteRelationModal && (
          <YesNoModal
            intl={intl}
            show={showDeleteRelationModal}
            title={intl.formatMessage(messages.DeleteRelation)}
            message={intl.formatMessage(messages.DeleteRelationConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                showDeleteRelationModal: false,
                activeRelation: undefined,
              }))
            }
            onSave={() => this.deleteRelation()}
          />
        )}
        {showUpdateTaskScheduleModal && (
          <UpdateTaskScheduleModal
            intl={intl}
            show={showUpdateTaskScheduleModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateTaskScheduleModal: false,
              }))
            }
            relationType={this.determineRelationTypeForGlobalAction()}
            onSave={({ action, model }) => {
              if (action === 'add') {
                return this.createTaskScheduleForSelectedRelations(model);
              }
              if (action === 'remove') {
                return this.removeTaskScheduleForSelectedRelations(model);
              }
            }}
          />
        )}
        {showCreateRegistrationModal && (
          <BatchRegistrationModal
            intl={intl}
            show={showCreateRegistrationModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showCreateRegistrationModal: false,
              }))
            }
            onSave={batchRegistrationModel => this.createBatchRegistrations(batchRegistrationModel)}
            countSelectedRelations={this.getCountSelectedRelations()}
          />
        )}
        {showUpdateRelationFieldModal && (
          <UpdateRelationFieldModal
            intl={intl}
            show={showUpdateRelationFieldModal}
            languages={relationLanguages}
            companyId={
              initialSearchParams?.relationFilters?.ownCompanies?.length === 1
                ? initialSearchParams.relationFilters.ownCompanies[0]
                : null
            }
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationFieldModal: false,
              }))
            }
            onSave={(field, endpoint) => this.updateRelationField(field, endpoint)}
          />
        )}
        {showSetInvoiceScheduleModal && (
          <SetInvoiceScheduleModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSetInvoiceScheduleModal: false,
              }))
            }
            onSave={invoiceSchedule => this.setInvoiceScheduleSelectedRelations(invoiceSchedule)}
          />
        )}
        {showSearchRelationsInPeppolNetworkModal && (
          <SearchRelationsInPeppolNetworkModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSearchRelationsInPeppolNetworkModal: false,
              }))
            }
            onSave={model =>
              this.executeBatchAction({
                action: 'SearchRelationsInPeppolNetwork',
                parameters: model,
              })
            }
          />
        )}
        {showCreatePlannedTaskModal && (
          <CreatePlannedTaskModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showCreatePlannedTaskModal: false,
              }))
            }
            onSave={task => this.createTaskForSelectedRelations(task)}
          />
        )}
        {showSendMailModal && (
          <SendEmailModal
            intl={intl}
            show
            languages={relationLanguages}
            emailContext={13}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSendMailModal: false,
              }))
            }
            onSave={emailModel => this.sendEmailForSelectedRelations(emailModel)}
          />
        )}
        {showUpdateRelationTeamModal && (
          <UpdateRelationTeamModal
            intl={intl}
            show={showUpdateRelationTeamModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationTeamModal: false,
              }))
            }
            onSave={data => this.updateRelationTeam(data)}
          />
        )}

        {showUpdateRelationTagsModal && (
          <UpdateRelationTagsModal
            intl={intl}
            show={showUpdateRelationTagsModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationTagsModal: false,
              }))
            }
            onSave={data => this.updateRelationTags(data)}
          />
        )}

        {showAddAssignmentModal && (
          <AddAssignmentModal
            intl={intl}
            onClose={() => this.setState({ showAddAssignmentModal: false })}
            onSave={data => this.addAssignmentToSelectedRelations(data)}
            relationType={this.determineRelationTypeForGlobalAction()}
          />
        )}
        {showUpdateRelationUserModal && (
          <UpdateRelationUserModal
            intl={intl}
            show={showUpdateRelationUserModal}
            onClose={() => this.setState({ showUpdateRelationUserModal: false })}
            onSave={data => this.updateRelationUser(data)}
          />
        )}
        {showGenerateDocumentsModal && (
          <BatchGenerateDocumentModal
            intl={intl}
            show
            languages={relationLanguages}
            onClose={() =>
              this.setState({
                showGenerateDocumentsModal: false,
              })
            }
            onSave={this.generateDocuments}
          />
        )}
        {showFiscalReportModal && (
          <FiscalReportModal
            intl={intl}
            show
            onClose={() => this.setState({ showFiscalReportModal: false })}
            onSave={this.createFiscalReport}
          />
        )}
        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectOverviewColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}

        <SimplePanel article="5434897596690" title={intl.formatMessage(messages.Relations)}>
          <div className="info-block container-fluid">
            <RelationOverviewFilters
              filters={initialSearchParams.relationFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              countSelectedRelations={this.getCountSelectedRelationsFormatted()}
              labels={labels}
            />
          </div>
        </SimplePanel>
        <div className="row btn-row hidden-xs">
          <div className="col-sm-12 content-right">
            <Button
              btnClassName="btn btn-xs btn-default"
              onClick={() => this.setState({ showColumnSelectionModal: true })}
            >
              <span className="margin5R">
                <FaCog className="react-icon" />
              </span>
              <FormattedMessage {...messages.ColumnConfig} />
            </Button>
            <ExportButton
              onCreateExcelReport={this.createExcelReport}
              onCreateAuthorizationReport={this.createAuthorizationReport}
              onCreateLinkedRelationsReport={this.createLinkedRelationsReport}
              onCreateFiscalReport={() => this.setState({ showFiscalReportModal: true })}
              onCreateCommunicationChannelReport={this.createCommunicationChannelReport}
              onCreateCommunicationSettingReport={this.createCommunicationSettingReport}
              disabled={!hasData}
              exporting={exporting}
            />

            <GlobalActionsButton
              onUpdateTaskSchedule={this.showUpdateTaskScheduleModal}
              onUpdateField={this.showUpdateRelationFieldModal}
              onCreateRegistration={this.showCreateRegistrationModal}
              onSetInvoiceSchedule={this.showSetInvoiceScheduleModal}
              onCreateTask={this.showCreatePlannedTaskModal}
              onUpdateTeam={() => this.setState({ showUpdateRelationTeamModal: true })}
              onUpdateTags={() => this.setState({ showUpdateRelationTagsModal: true })}
              onSendMail={this.openEmailModal}
              onAddAssignment={this.showAddAssignmentModal}
              onUpdateRelationUser={this.showUpdateRelationUserModal}
              onGenerateDocuments={this.openGenerateDocumentsModal}
              onSearchOnPeppolNetwork={() =>
                this.setState({ showSearchRelationsInPeppolNetworkModal: true })
              }
              disabled={this.isGlobalActionsDisabled()}
            />
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid relation-overview-table"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => (
            <RelationOverviewRow
              key={item.id}
              relation={item}
              intl={intl}
              columnStatus={columnStatus}
              handleRelationSelect={this.handleRelationSelect}
              showDeleteRelationConfirmation={this.showDeleteRelationConfirmation}
              restoreRelation={this.restoreRelation}
              goToInteractions={this.goToInteractions}
            />
          )}
          noDataLabel={<FormattedMessage {...messages.NoRelationsFound} />}
        >
          <ColumnHeader
            className="checkbox-column"
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={() => this.handleSelectAllRelations()}
                value={allRelationsSelected}
              />
            }
          />

          <ColumnHeader title={intl.formatMessage(messages.Name)} sortKey="displayname" />

          {columnDefinitions.map(column => (
            <ColumnHeader
              key={column.id}
              title={intl.formatMessage(column.message)}
              sortKey={column.disableSorting ? null : column.sortKey || column.id}
              className={column.className}
              visible={columnStatus[column.id]}
            />
          ))}

          <ColumnHeader
            title={intl.formatMessage(messages.Actions)}
            id="relationOverviewActions"
            className="text-nowrap"
          />
        </ScrollableTable>
      </div>
    );
  }

  static propTypes = {
    intl: intlShape.isRequired,
  };

  static defaultProps = {};
}

export default showWhenPermission(
  'relation',
  'read',
)(injectStateService(injectIntl(RelationOverviewContainer)));
